<script setup>
import { reactive, ref, shallowRef, watch } from 'vue';
import UiDialog from '@/components/ui/UiDialog.vue';
import UiFormField from '@/components/ui/UiFormField.vue';
import { apiRequest, assertOk, errorMessage } from '@/api/http';
import { useModuleOptions } from '@/composables/useModuleOptions';
import { useToast } from '@/composables/useToast';
import { LEVEL_OPTIONS, RoleCode } from '@/constants/accounts';
import {
  email,
  matches,
  required,
  strongPassword,
} from '@/utils/validation';

// Admin creates a faculty account directly (approved, no OTP). One form
// instead of the old three-step wizard.
const open = defineModel({ type: Boolean, default: false });
const emit = defineEmits(['saved']);

const toast = useToast();
const { findRoleId, designationOptions, withoutImpliedTeacher, withImpliedTeacher } = useModuleOptions();

const formRef = ref(null);
const saving = shallowRef(false);
const reveal = shallowRef(false);
const emailTaken = shallowRef(false);

const blank = () => ({
  fname: '',
  mname: '',
  lname: '',
  email: '',
  password: '',
  confirm: '',
  levelScope: null,
  // New faculty accounts start as Teacher.
  designations: [findRoleId(RoleCode.Teacher)].filter(Boolean),
});
const form = reactive(blank());

watch(open, (isOpen) => {
  if (!isOpen) return;
  Object.assign(form, blank());
  emailTaken.value = false;
  reveal.value = false;
  formRef.value?.resetValidation();
});

async function checkEmail() {
  emailTaken.value = false;
  if (email(form.email) !== true || !form.email) return;
  try {
    emailTaken.value = !!(await apiRequest(
      'get',
      `/auth/checkEmailIfExist/${encodeURIComponent(form.email)}`,
    ));
  } catch {
    emailTaken.value = false;
  }
}

// Picking Adviser drops the Subject Teacher chip: it's included (2 in 1).
watch(
  () => form.designations,
  (picked) => {
    const shown = withoutImpliedTeacher(picked);
    if (shown.length !== (picked || []).length) form.designations = shown;
  },
);

async function save() {
  const { valid } = await formRef.value.validate();
  if (!valid || emailTaken.value) return;
  saving.value = true;
  try {
    const body = await apiRequest('post', '/auth/addUserByAdmin', {
      fname: form.fname.trim(),
      mname: form.mname.trim(),
      lname: form.lname.trim(),
      email: form.email.trim(),
      password: form.password,
      levelScope: form.levelScope,
      subModules: JSON.stringify(withImpliedTeacher(form.designations)),
    });
    assertOk(body, [200, 201]);
    toast.success(body.msg || 'Account created.');
    open.value = false;
    emit('saved');
  } catch (e) {
    toast.error(errorMessage(e, 'Could not create the account.'));
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <UiDialog
    v-model="open"
    title="Add account"
    description="Create a faculty account. It's approved right away. Share the password with them; they'll choose their own when they first sign in."
    size="lg"
  >
    <v-form ref="formRef" class="form-grid" @submit.prevent="save">
      <UiFormField label="First name" required v-slot="{ id }">
        <v-text-field
          :id="id"
          v-model="form.fname"
          :rules="[required]"
          hide-details="auto"
        />
      </UiFormField>
      <UiFormField label="Last name" required v-slot="{ id }">
        <v-text-field
          :id="id"
          v-model="form.lname"
          :rules="[required]"
          hide-details="auto"
        />
      </UiFormField>
      <UiFormField label="Middle name" v-slot="{ id }">
        <v-text-field :id="id" v-model="form.mname" hide-details="auto" />
      </UiFormField>
      <UiFormField label="Email" required v-slot="{ id }">
        <v-text-field
          :id="id"
          v-model="form.email"
          type="email"
          :rules="[required, email]"
          :error-messages="emailTaken ? 'This email is already in use.' : []"
          hide-details="auto"
          autocomplete="off"
          @blur="checkEmail"
          @update:model-value="emailTaken = false"
        />
      </UiFormField>

      <UiFormField label="Password" required v-slot="{ id }">
        <v-text-field
          :id="id"
          v-model="form.password"
          :type="reveal ? 'text' : 'password'"
          :append-inner-icon="reveal ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
          :rules="[required, strongPassword]"
          hide-details="auto"
          autocomplete="new-password"
          @click:append-inner="reveal = !reveal"
        />
      </UiFormField>
      <UiFormField label="Confirm password" required v-slot="{ id }">
        <v-text-field
          :id="id"
          v-model="form.confirm"
          :type="reveal ? 'text' : 'password'"
          :rules="[
            required,
            matches(() => form.password, 'Passwords do not match.'),
          ]"
          hide-details="auto"
          autocomplete="new-password"
        />
      </UiFormField>

      <UiFormField
        label="School level"
        description="JHS/SHS Admin and Prefect of Discipline roles set this automatically."
        required
        v-slot="{ id }"
      >
        <v-select
          :id="id"
          v-model="form.levelScope"
          :items="LEVEL_OPTIONS"
          item-value="value"
          item-title="title"
          placeholder="Select a level"
          :rules="[required]"
          hide-details="auto"
        />
      </UiFormField>
      <UiFormField label="Designations" required v-slot="{ id }">
        <v-autocomplete
          :id="id"
          v-model="form.designations"
          :items="designationOptions(form.designations)"
          item-value="id"
          item-title="description"
          :rules="[required]"
          multiple
          chips
          closable-chips
          hide-details="auto"
        />
      </UiFormField>
    </v-form>

    <template #footer="{ close }">
      <v-btn variant="outlined" @click="close">Cancel</v-btn>
      <v-btn color="primary" :loading="saving" @click="save">
        Create account
      </v-btn>
    </template>
  </UiDialog>
</template>
